import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AllocationRow } from '../../types/tradeBooking';
import { TradingEntity } from '../../services/api/clientService';
import { GroupingsConfig } from '../../types/groupings';
import { formatAmountDisplay, parseAmountInput } from '../../utils/amountFormatter';
import AllocationExcelUpload from './AllocationExcelUpload';
import './AllocationPanel.css';

interface AllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentAmount: number | null;
  tradingEntities: TradingEntity[];
  groupingsConfig: GroupingsConfig | null;
  allocationRows: AllocationRow[];
  onAllocationChange: (rows: AllocationRow[]) => void;
  disabled: boolean;
}

const AllocationModal: React.FC<AllocationModalProps> = ({
  isOpen,
  onClose,
  parentAmount,
  tradingEntities,
  groupingsConfig,
  allocationRows,
  onAllocationChange,
  disabled
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // Track which amount cell is being edited (show raw number while editing)
  const [editingAmountIndex, setEditingAmountIndex] = useState<number | null>(null);
  const [editingAmountValue, setEditingAmountValue] = useState('');

  const hasG2 = groupingsConfig?.grouping2Label ? true : false;
  const hasG3 = groupingsConfig?.grouping3Label ? true : false;

  // Get filtered grouping values for a specific entity
  const getFilteredGroupingValues = useCallback((entityId: string, groupingNumber: 1 | 2 | 3) => {
    if (!groupingsConfig || !entityId) return [];

    const entityMapping = groupingsConfig.entityMappings.find(m => m.entityId === entityId);
    if (!entityMapping) return [];

    const allowedIds = groupingNumber === 1
      ? entityMapping.grouping1ValueIds
      : groupingNumber === 2
      ? entityMapping.grouping2ValueIds
      : entityMapping.grouping3ValueIds;
    const allValues = groupingNumber === 1
      ? groupingsConfig.grouping1Values
      : groupingNumber === 2
      ? groupingsConfig.grouping2Values
      : groupingsConfig.grouping3Values;

    return allValues.filter(v => v.isActive && allowedIds.includes(v.id));
  }, [groupingsConfig]);

  const addRow = () => {
    const newRow: AllocationRow = {
      entityId: '',
      entityName: '',
      entityTaxId: '',
      grouping1: '',
      grouping2: '',
      grouping3: '',
      amount: null,
      percentage: null,
      hedgeObject: '',
      notes: '',
    };
    onAllocationChange([...allocationRows, newRow]);
  };

  const removeRow = (index: number) => {
    const updated = allocationRows.filter((_, i) => i !== index);
    onAllocationChange(updated);
  };

  const copyRow = (index: number) => {
    const source = allocationRows[index];
    const copy: AllocationRow = { ...source, amount: null, percentage: null };
    const updated = [...allocationRows];
    updated.splice(index + 1, 0, copy);
    onAllocationChange(updated);
  };

  const updateRow = (index: number, field: keyof AllocationRow, value: any) => {
    const updated = [...allocationRows];
    const row = { ...updated[index] };

    if (field === 'entityId') {
      const entity = tradingEntities.find(e => e.id === value);
      row.entityId = value;
      row.entityName = entity?.name || '';
      row.entityTaxId = entity?.taxId || '';
      row.grouping1 = '';
      row.grouping2 = '';
      row.grouping3 = '';
    } else if (field === 'amount') {
      const numValue = value === '' ? null : Number(value);
      row.amount = numValue;
      if (numValue !== null && parentAmount && parentAmount > 0) {
        row.percentage = Math.round((numValue / parentAmount) * 10000) / 100;
      } else {
        row.percentage = null;
      }
    } else if (field === 'percentage') {
      const numValue = value === '' ? null : Number(value);
      row.percentage = numValue;
      if (numValue !== null && parentAmount && parentAmount > 0) {
        row.amount = Math.round((numValue / 100) * parentAmount * 100) / 100;
      } else {
        row.amount = null;
      }
    } else if (field === 'grouping1') {
      row.grouping1 = value;
      if (groupingsConfig?.grouping2Mode === 'subgrouping') {
        row.grouping2 = '';
        if (groupingsConfig?.grouping3Mode === 'subgrouping') {
          row.grouping3 = '';
        }
      }
    } else if (field === 'grouping2') {
      row.grouping2 = value;
      if (groupingsConfig?.grouping3Mode === 'subgrouping') {
        row.grouping3 = '';
      }
    } else {
      (row as any)[field] = value;
    }

    updated[index] = row;
    onAllocationChange(updated);
  };

  // Get G2 values filtered by G1 selection in subgrouping mode
  const getG2ValuesForRow = useCallback((row: AllocationRow) => {
    if (!groupingsConfig || !row.entityId) return [];

    if (groupingsConfig.grouping2Mode === 'subgrouping') {
      const entityMapping = groupingsConfig.entityMappings.find(m => m.entityId === row.entityId);
      if (!entityMapping || !row.grouping1) return [];

      const g1Value = groupingsConfig.grouping1Values.find(v => v.name === row.grouping1);
      if (!g1Value) return [];

      const g2Ids = entityMapping.g1ToG2Mappings?.[g1Value.id] || [];
      return groupingsConfig.grouping2Values.filter(v => v.isActive && g2Ids.includes(v.id));
    }

    return getFilteredGroupingValues(row.entityId, 2);
  }, [groupingsConfig, getFilteredGroupingValues]);

  // Get G3 values filtered by G2 selection in subgrouping mode
  const getG3ValuesForRow = useCallback((row: AllocationRow) => {
    if (!groupingsConfig || !row.entityId) return [];

    if (groupingsConfig.grouping3Mode === 'subgrouping') {
      const entityMapping = groupingsConfig.entityMappings.find(m => m.entityId === row.entityId);
      if (!entityMapping || !row.grouping2) return [];

      const g2Value = groupingsConfig.grouping2Values.find(v => v.name === row.grouping2);
      if (!g2Value) return [];

      const g3Ids = entityMapping.g2ToG3Mappings?.[g2Value.id] || [];
      return groupingsConfig.grouping3Values.filter(v => v.isActive && g3Ids.includes(v.id));
    }

    return getFilteredGroupingValues(row.entityId, 3);
  }, [groupingsConfig, getFilteredGroupingValues]);

  // Calculate totals
  const totalAmount = allocationRows.reduce((sum, row) => sum + (row.amount || 0), 0);
  const totalPercentage = allocationRows.reduce((sum, row) => sum + (row.percentage || 0), 0);
  const rowCount = allocationRows.length;

  // 3-state: under / balanced / over
  const balanceStatus: 'under' | 'balanced' | 'over' = parentAmount
    ? Math.abs(totalAmount - parentAmount) < 0.01
      ? 'balanced'
      : totalAmount > parentAmount ? 'over' : 'under'
    : 'under';

  // Add an empty row when modal opens with no rows
  useEffect(() => {
    if (isOpen && allocationRows.length === 0) {
      addRow();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog allocation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('tradeBooking.allocation.title')}</h2>
        </div>

        <div className="modal-body allocation-modal-body">
              <table className="allocation-table">
                <thead>
                  <tr>
                    <th className="col-entity">{t('tradeBooking.allocation.entity')}</th>
                    <th className="col-g1">{groupingsConfig?.grouping1Label || t('tradeBooking.classification.grouping1')}</th>
                    {hasG2 && (
                      <th className="col-g2">{groupingsConfig?.grouping2Label || t('tradeBooking.classification.grouping2')}</th>
                    )}
                    {hasG3 && (
                      <th className="col-g3">{groupingsConfig?.grouping3Label || t('tradeBooking.classification.grouping3')}</th>
                    )}
                    <th className="col-hedge">{t('tradeBooking.allocation.hedgeObject')}</th>
                    <th className="col-notes">{t('tradeBooking.allocation.notes')}</th>
                    <th className="col-amount">{t('tradeBooking.allocation.amount')}</th>
                    <th className="col-pct">%</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {allocationRows.map((row, index) => (
                    <tr key={index}>
                      <td className="col-entity">
                        <select
                          className="alloc-select"
                          value={row.entityId}
                          onChange={(e) => updateRow(index, 'entityId', e.target.value)}
                          disabled={disabled}
                        >
                          <option value="">{t('tradeBooking.allocation.selectEntity')}</option>
                          {tradingEntities.map(entity => (
                            <option key={entity.id} value={entity.id}>
                              {entity.name}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="col-g1">
                        <select
                          className="alloc-select"
                          value={row.grouping1}
                          onChange={(e) => updateRow(index, 'grouping1', e.target.value)}
                          disabled={disabled || !row.entityId}
                        >
                          <option value="">{t('tradeBooking.classification.select')}</option>
                          {getFilteredGroupingValues(row.entityId, 1).map(v => (
                            <option key={v.id} value={v.name}>{v.name}</option>
                          ))}
                        </select>
                      </td>

                      {hasG2 && (
                        <td className="col-g2">
                          <select
                            className="alloc-select"
                            value={row.grouping2}
                            onChange={(e) => updateRow(index, 'grouping2', e.target.value)}
                            disabled={disabled || !row.entityId || (groupingsConfig?.grouping2Mode === 'subgrouping' && !row.grouping1)}
                          >
                            <option value="">{t('tradeBooking.classification.select')}</option>
                            {getG2ValuesForRow(row).map(v => (
                              <option key={v.id} value={v.name}>{v.name}</option>
                            ))}
                          </select>
                        </td>
                      )}

                      {hasG3 && (
                        <td className="col-g3">
                          <select
                            className="alloc-select"
                            value={row.grouping3}
                            onChange={(e) => updateRow(index, 'grouping3', e.target.value)}
                            disabled={disabled || !row.entityId || (groupingsConfig?.grouping3Mode === 'subgrouping' && !row.grouping2)}
                          >
                            <option value="">{t('tradeBooking.classification.select')}</option>
                            {getG3ValuesForRow(row).map(v => (
                              <option key={v.id} value={v.name}>{v.name}</option>
                            ))}
                          </select>
                        </td>
                      )}

                      <td className="col-hedge">
                        <input
                          type="text"
                          className="alloc-input"
                          value={row.hedgeObject}
                          onChange={(e) => updateRow(index, 'hedgeObject', e.target.value)}
                          disabled={disabled}
                          placeholder=""
                          maxLength={50}
                        />
                      </td>

                      <td className="col-notes">
                        <input
                          type="text"
                          className="alloc-input"
                          value={row.notes}
                          onChange={(e) => updateRow(index, 'notes', e.target.value)}
                          disabled={disabled}
                          placeholder=""
                          maxLength={200}
                        />
                      </td>

                      <td className="col-amount">
                        <input
                          type="text"
                          className="alloc-input"
                          value={editingAmountIndex === index
                            ? editingAmountValue
                            : (row.amount != null ? formatAmountDisplay(row.amount, locale, 2) : '')
                          }
                          onChange={(e) => {
                            setEditingAmountValue(e.target.value);
                          }}
                          onFocus={() => {
                            setEditingAmountIndex(index);
                            setEditingAmountValue(row.amount != null ? String(row.amount) : '');
                          }}
                          onBlur={() => {
                            const trimmed = editingAmountValue.trim();
                            let parsed: number | null;

                            // % suffix: convert percentage to amount (e.g. "40%" → 40% of parent)
                            if (trimmed.endsWith('%') && parentAmount && parentAmount > 0) {
                              const pct = parseFloat(trimmed.slice(0, -1).trim());
                              parsed = (!isNaN(pct) && pct > 0 && pct <= 100)
                                ? Math.round((pct / 100) * parentAmount * 100) / 100
                                : null;
                            } else {
                              parsed = parseAmountInput(editingAmountValue);
                            }

                            updateRow(index, 'amount', parsed != null ? String(parsed) : '');
                            setEditingAmountIndex(null);
                          }}
                          disabled={disabled}
                          placeholder="0"
                        />
                      </td>

                      <td className="col-pct">
                        <input
                          type="number"
                          className="alloc-input"
                          value={row.percentage ?? ''}
                          onChange={(e) => updateRow(index, 'percentage', e.target.value)}
                          disabled={disabled}
                          placeholder="0"
                          min="0"
                          max="100"
                          step="any"
                        />
                      </td>

                      <td className="col-actions">
                        <button
                          type="button"
                          className="alloc-action-btn"
                          onClick={() => copyRow(index)}
                          disabled={disabled}
                          title={t('tradeBooking.allocation.copyRow')}
                        >
                          ⧉
                        </button>
                        <button
                          type="button"
                          className="alloc-delete-btn"
                          onClick={() => removeRow(index)}
                          disabled={disabled}
                          title={t('tradeBooking.allocation.removeRow')}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`allocation-totals ${balanceStatus}`}>
                    <td colSpan={(hasG2 ? 5 : 4) + (hasG3 ? 1 : 0)} className="totals-label">
                      {t('tradeBooking.allocation.total')}
                    </td>
                    <td className="col-amount">
                      <span className="totals-value">{formatAmountDisplay(totalAmount, locale, 0)}</span>
                    </td>
                    <td className="col-pct">
                      <span className="totals-value">{Math.round(totalPercentage * 100) / 100}%</span>
                    </td>
                    <td className="col-actions"></td>
                  </tr>
                  {parentAmount != null && (
                    <tr className="allocation-parent-row">
                      <td colSpan={(hasG2 ? 5 : 4) + (hasG3 ? 1 : 0)} className="totals-label">
                        {t('tradeBooking.allocation.tradeAmount')}
                      </td>
                      <td className="col-amount">
                        <span className="parent-amount-value">{formatAmountDisplay(parentAmount, locale, 0)}</span>
                      </td>
                      <td className="col-pct">
                        <span className="parent-amount-value">100%</span>
                      </td>
                      <td className="col-actions"></td>
                    </tr>
                  )}
                </tfoot>
              </table>

              <div className="allocation-below-table">
                <AllocationExcelUpload
                  tradingEntities={tradingEntities}
                  groupingsConfig={groupingsConfig}
                  parentAmount={parentAmount}
                  onRowsParsed={(rows) => onAllocationChange(rows)}
                  disabled={disabled}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm allocation-add-row-btn"
                  onClick={addRow}
                  disabled={disabled}
                >
                  + {t('tradeBooking.allocation.addRow')}
                </button>
              </div>
        </div>

        <div className="modal-footer">
          {rowCount > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onAllocationChange([])}
            >
              {t('tradeBooking.allocation.clearAll')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={onClose}
          >
            {t('tradeBooking.allocation.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocationModal;
